import { useState, useEffect, useRef } from "react";
import { View, Text, Animated, Easing, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { differenceInSeconds } from "date-fns";
import LottieView from "lottie-react-native";
import { getLottieSource } from "@/lib/lottie-registry";

/**
 * Props for RevealCountdown
 */
interface RevealCountdownProps {
  revealAt: string;
  onReveal?: () => void;
  lottieAnimation?: string | null;
  title?: string;
  subtitle?: string;
  isAdmin?: boolean;
}

/**
 * Format countdown time
 */
const formatCountdown = (revealAt: Date): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
} => {
  const now = new Date();

  if (revealAt <= now) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }

  const totalSeconds = differenceInSeconds(revealAt, now);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds, isExpired: false };
};

/**
 * Countdown number display
 */
const CountdownDigit = ({
  value,
  label,
  colors,
}: {
  value: number;
  label: string;
  colors: ReturnType<typeof useThemeColors>;
}) => (
  <View className="items-center mx-2">
    <View
      className="w-16 h-16 rounded-xl items-center justify-center"
      style={{ backgroundColor: colors.primaryLight }}
    >
      <Text className="text-2xl font-bold" style={{ color: colors.primary }}>
        {value.toString().padStart(2, "0")}
      </Text>
    </View>
    <Text className="text-xs mt-1.5 uppercase" style={{ color: colors.textSecondary }}>
      {label}
    </Text>
  </View>
);

/**
 * RevealCountdown Component
 * Displays a countdown timer until reveal
 */
export const RevealCountdown = ({
  revealAt,
  onReveal,
  lottieAnimation,
  title = "Onthulling over...",
  subtitle,
  isAdmin = false,
}: RevealCountdownProps) => {
  const colors = useThemeColors();
  const [countdown, setCountdown] = useState(() => formatCountdown(new Date(revealAt)));
  const pulseAnim = useRef(new Animated.Value(1)).current;

  /**
   * Update countdown every second
   */
  useEffect(() => {
    const timer = setInterval(() => {
      const newCountdown = formatCountdown(new Date(revealAt));
      setCountdown(newCountdown);

      if (newCountdown.isExpired) {
        clearInterval(timer);
        onReveal?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [revealAt, onReveal]);

  /**
   * Pulse animation for the container
   */
  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.02,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    );

    pulse.start();

    return () => pulse.stop();
  }, [pulseAnim]);

  if (countdown.isExpired) {
    return (
      <View className="items-center p-6">
        {lottieAnimation ? (
          <LottieView
            source={getLottieSource(lottieAnimation) || getLottieSource("cheers")}
            autoPlay
            loop={false}
            style={{ width: 200, height: 200 }}
          />
        ) : (
          <View
            className="w-20 h-20 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.successLight }}
          >
            <Text className="text-4xl">🎉</Text>
          </View>
        )}
        <Text className="text-xl font-bold mt-4" style={{ color: colors.text }}>
          Onthuld!
        </Text>
      </View>
    );
  }

  return (
    <Animated.View
      className="rounded-2xl p-6 mx-4"
      style={{
        backgroundColor: colors.surface,
        transform: [{ scale: pulseAnim }],
      }}
    >
      {/* Title */}
      <View className="items-center mb-4">
        <Text className="text-lg font-bold" style={{ color: colors.text }}>
          {title}
        </Text>
        {subtitle && (
          <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
            {subtitle}
          </Text>
        )}
      </View>

      {/* Countdown digits */}
      <View className="flex-row justify-center items-center">
        {countdown.days > 0 && (
          <>
            <CountdownDigit value={countdown.days} label="dagen" colors={colors} />
            <Text className="text-2xl font-bold" style={{ color: colors.textTertiary }}>
              :
            </Text>
          </>
        )}
        <CountdownDigit value={countdown.hours} label="uren" colors={colors} />
        <Text className="text-2xl font-bold" style={{ color: colors.textTertiary }}>
          :
        </Text>
        <CountdownDigit value={countdown.minutes} label="min" colors={colors} />
        <Text className="text-2xl font-bold" style={{ color: colors.textTertiary }}>
          :
        </Text>
        <CountdownDigit value={countdown.seconds} label="sec" colors={colors} />
      </View>

      {/* Mystery icon */}
      <View className="items-center mt-4">
        <View
          className="w-12 h-12 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.primaryLight }}
        >
          <Text className="text-xl">🔮</Text>
        </View>
      </View>

      {/* Admin manual reveal button */}
      {isAdmin && (
        <TouchableOpacity
          onPress={onReveal}
          className="mt-4 flex-row items-center justify-center py-3 px-4 rounded-xl"
          style={{ backgroundColor: colors.warning }}
          accessibilityRole="button"
          accessibilityLabel="Nu onthullen"
        >
          <Ionicons name="eye" size={20} color="#FFFFFF" />
          <Text className="ml-2 font-semibold text-white">Nu onthullen</Text>
        </TouchableOpacity>
      )}
    </Animated.View>
  );
};

export default RevealCountdown;

